import type {
  PreviewProfileTypeBlockDraftRequest,
  PreviewProfileTypeBlockResponse,
  PreviewTypeRemovalResponse,
  ProfileTypeStats,
  RemoveTypeResponse,
  RunProfileTypeBlockResponse,
  SaveProfileTypeBlockRequest,
  TypeRemoval,
  TypeRemovalScope,
  UndoTypeRemovalResponse,
} from '@shared/schemas';

import api from '@/shared/api/api-client';

import type { ProfileTypeBlock } from '../domain/profile-type.model';
import type { ProfileTypesGateway } from './profile-types.gateway';

const BASE = '/profile-type-blocks';

/**
 * The type's path. Names are free text ("CGP (Wealth Manager)",
 * "Bank / Institutional Investor") so they are always encoded: an unencoded slash
 * would read as a second path segment.
 */
const typePath = (name: string) => `${BASE}/${encodeURIComponent(name)}`;

/**
 * Real implementation over the shared api client; the single place that knows the
 * profile-type-block endpoints. The client already unwraps the `{ success, data }`
 * envelope, so these read as the DTOs themselves.
 */
export const httpProfileTypesGateway: ProfileTypesGateway = {
  loadBlocks: () => api.get<ProfileTypeBlock[]>(BASE),

  saveBlock: (name: string, payload: SaveProfileTypeBlockRequest) =>
    api.put<ProfileTypeBlock>(typePath(name), payload),

  // 204, no body; the client answers `undefined`, which is why this resolves void.
  clearBlock: async (name: string) => {
    await api.delete<void>(typePath(name));
  },

  previewDraft: (payload: PreviewProfileTypeBlockDraftRequest) =>
    api.post<PreviewProfileTypeBlockResponse>(`${BASE}/preview`, payload),

  previewRun: (name: string) => api.post<PreviewProfileTypeBlockResponse>(`${typePath(name)}/run/preview`),

  runBlock: (name: string) => api.post<RunProfileTypeBlockResponse>(`${typePath(name)}/run`),

  loadStats: () => api.get<ProfileTypeStats>(`${BASE}/stats`),

  // The endpoint answers the whole rows; the priority card only needs the order.
  loadTypeOrder: async () => {
    const types = await api.get<{ name: string }[]>('/profile-types');
    return types.map((t) => t.name);
  },

  saveTypeOrder: async (names: string[]) => {
    const res = await api.put<{ profiles_recomputed: number }>('/profile-types/order', { names });
    return res.profiles_recomputed;
  },

  previewRemoval: (name: string) => api.post<PreviewTypeRemovalResponse>(`${typePath(name)}/removal/preview`),

  removeType: (name: string, scope: TypeRemovalScope) =>
    api.post<RemoveTypeResponse>(`${typePath(name)}/removal`, { scope }),

  loadRemovals: () => api.get<TypeRemoval[]>('/profile-type-removals'),

  undoRemoval: (id: string) => api.post<UndoTypeRemovalResponse>(`/profile-type-removals/${id}/undo`),
};
